/**
 * PurchaseOrdersPage.tsx
 *
 * Purchase Orders management page with paginated list, status filtering,
 * create PO modal with line items, submit/receive/cancel workflows.
 *
 * Uses standard ListPageLayout + FilterSidebar + DataTable pattern.
 */
import { useState, useMemo, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Download,
  Plus,
  Trash2,
  Loader2,
} from 'lucide-react';

import {
  listPurchaseOrders,
  getPurchaseOrderStats,
  createPurchaseOrder,
  submitPurchaseOrder,
  cancelPurchaseOrder,
  receivePurchaseOrder,
  type PurchaseOrder,
  type CreatePOPayload,
  type POStatus,
  type ReceiveItemsPayload,
} from '@/services/purchase-orders.service';
import { getErrorMessage } from '@/services/apiClient';
import { listProducts, type Product } from '@/services/inventory.service';
import { listVendors, type Vendor } from '@/services/vendors.service';
import { FilterSidebar } from '@/components/layout/FilterSidebar';
import { ListPageLayout } from '@/components/layout/ListPageLayout';
import { PageHeader } from '@/components/layout/PageHeader';
import { DataTable } from '@/components/ui/DataTable';
import type { DataTableAction, DataTableColumn } from '@/components/ui/DataTable';
import { MetricCardGrid } from '@/components/ui/MetricCardGrid';
import { StatCard } from '@/components/ui/StatCard';
import { Button } from '@/components/ui/Button';
import { DatePicker } from '@/components/ui/DatePicker';
import { PHONE_SHEET_PANEL, PHONE_SHEET_WRAPPER } from '@/components/ui/Modal';
import { useBodyScrollLock } from '@/hooks/useBodyScrollLock';
import { useIsBelow } from '@/hooks/useBreakpoint';
import { useVisualViewportVars } from '@/hooks/useVisualViewportVars';
import { useAsyncExport } from '@/hooks/useAsyncExport';
import { usePageContext } from '@/hooks/usePageContext';
import type { FilterConfig, FilterOption, FilterValue } from '@/types/filters.types';

// ─── Constants ────────────────────────────────────────────────────────────────

const PER_PAGE = 25;

const FILTER_CONFIGS: FilterConfig[] = [
  { key: 'search', label: 'Search', type: 'search', allLabel: 'Search purchase orders...' },
  { key: 'status', label: 'Status', type: 'select', allLabel: 'All statuses' },
  { key: 'vendor', label: 'Vendor', type: 'select', allLabel: 'All vendors' },
];

const STATUS_BADGE_CLASSES: Record<POStatus, string> = {
  draft:              'bg-slate-100 text-slate-700',
  submitted:          'bg-blue-100 text-blue-700',
  partially_received: 'bg-amber-100 text-amber-700',
  received:           'bg-emerald-100 text-emerald-700',
  cancelled:          'bg-red-100 text-red-700',
};

const STATUS_LABELS: Record<POStatus, string> = {
  draft:              'Draft',
  submitted:          'Submitted',
  partially_received: 'Partially Received',
  received:           'Received',
  cancelled:          'Cancelled',
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

const currencyFormatter = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

function formatCurrency(value: number | null | undefined): string {
  if (value == null) return currencyFormatter.format(0);
  return currencyFormatter.format(value);
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '--';
  try {
    return new Date(iso).toLocaleDateString();
  } catch {
    return iso;
  }
}

function statusLabel(status: POStatus): string {
  return STATUS_LABELS[status] ?? status;
}

// ─── Shared UI classes (for modals) ──────────────────────────────────────────

const inputCls =
  'border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 w-full disabled:opacity-50 disabled:bg-slate-50';
const labelCls = 'block text-sm font-medium text-slate-700 mb-1';
const btnPrimary =
  'inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors disabled:opacity-50';
const btnSecondary =
  'inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium border border-slate-300 rounded-lg hover:bg-slate-50 text-slate-700 transition-colors disabled:opacity-50';

// ─── Line item type for the create form ───────────────────────────────────────

interface LineItem {
  key: number;
  productId: number | '';
  quantity: number | '';
  unitCost: number | '';
}

/** Order and receive quantities are whole units; the API refuses a fraction. */
const WHOLE_QUANTITIES_MESSAGE = 'Enter quantities as whole numbers — part of a unit can’t be ordered or received.';

let lineItemKey = 0;
function newLineItem(): LineItem {
  return { key: ++lineItemKey, productId: '', quantity: '', unitCost: '' };
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function PurchaseOrdersPage() {
  const queryClient = useQueryClient();
  usePageContext('Purchase Orders');

  // ── Filter + pagination state ─────────────────────────────────────────────
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [vendorFilter, setVendorFilter] = useState<string>('');
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  // Debounce search so we don't hit the API on every keystroke.
  useEffect(() => {
    const handle = window.setTimeout(() => {
      setSearchQuery(searchInput.trim());
      setPage(1);
    }, 300);
    return () => window.clearTimeout(handle);
  }, [searchInput]);

  // ── Modal state ───────────────────────────────────────────────────────────
  const navigate = useNavigate();
  const [createOpen, setCreateOpen] = useState(false);
  const [receiveTarget, setReceiveTarget] = useState<PurchaseOrder | null>(null);
  const [cancelTarget, setCancelTarget] = useState<PurchaseOrder | null>(null);
  const { exporting, startExport, exportModal } = useAsyncExport({
    entityType: 'purchase-orders',
    entityLabel: 'purchase orders',
  });

  // ── Queries ───────────────────────────────────────────────────────────────

  const poQuery = useQuery({
    queryKey: ['po-list', page, statusFilter, vendorFilter, searchQuery],
    queryFn: () =>
      listPurchaseOrders({
        status: (statusFilter as POStatus) || undefined,
        vendorId: vendorFilter ? Number(vendorFilter) : undefined,
        search: searchQuery || undefined,
        page,
        perPage: PER_PAGE,
      }),
    placeholderData: (prev) => prev,
  });

  const statsQuery = useQuery({
    queryKey: ['po-stats'],
    queryFn: getPurchaseOrderStats,
    staleTime: 30_000,
  });

  const vendorsQuery = useQuery({
    queryKey: ['vendors-all'],
    queryFn: () => listVendors({ perPage: 500, active: true }),
    staleTime: 5 * 60 * 1000,
  });

  const productsQuery = useQuery({
    queryKey: ['products-all'],
    queryFn: () => listProducts({ perPage: 1000, active: true }),
    staleTime: 5 * 60 * 1000,
  });

  const poList = poQuery.data?.data ?? [];
  const meta = poQuery.data?.meta;
  const vendors = vendorsQuery.data?.data ?? [];
  const vendorsLoading = vendorsQuery.isLoading;
  const products = productsQuery.data?.data ?? [];

  const vendorMap = useMemo(
    () => new Map(vendors.map((v) => [v.vendorId, v])),
    [vendors],
  );

  // ── Stat counts (server-wide, not scoped to the current page) ─────────────
  const stats = statsQuery.data;

  // ── Invalidation helper ───────────────────────────────────────────────────

  const invalidatePOs = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['po-list'] });
    queryClient.invalidateQueries({ queryKey: ['po-stats'] });
  }, [queryClient]);

  function handleExport() {
    startExport({
      status: (statusFilter as POStatus) || undefined,
      vendorId: vendorFilter ? Number(vendorFilter) : undefined,
      search: searchQuery || undefined,
    });
  }

  // ── Submit mutation ───────────────────────────────────────────────────────

  const submitMutation = useMutation({
    mutationFn: (poId: number) => submitPurchaseOrder(poId),
    onSuccess: () => invalidatePOs(),
  });

  // ── Cancel mutation ───────────────────────────────────────────────────────

  const cancelMutation = useMutation({
    mutationFn: (poId: number) => cancelPurchaseOrder(poId),
    onSuccess: () => {
      invalidatePOs();
      setCancelTarget(null);
    },
  });

  // ── Sidebar filter plumbing ─────────────────────────────────────────────

  const sidebarOptions = useMemo<Record<string, FilterOption[]>>(
    () => ({
      status: [
        { value: 'draft', label: 'Draft' },
        { value: 'submitted', label: 'Submitted' },
        { value: 'partially_received', label: 'Partially Received' },
        { value: 'received', label: 'Received' },
        { value: 'cancelled', label: 'Cancelled' },
      ],
      vendor: vendors.map((v) => ({ value: String(v.vendorId), label: v.name })),
    }),
    [vendors],
  );

  const sidebarValues = useMemo<Record<string, FilterValue>>(
    () => ({
      search: searchInput ? [searchInput] : [],
      status: statusFilter ? [statusFilter] : [],
      vendor: vendorFilter ? [vendorFilter] : [],
    }),
    [searchInput, statusFilter, vendorFilter],
  );

  const handleSidebarChange = useCallback((filterKey: string, value: FilterValue) => {
    switch (filterKey) {
      case 'search':
        setSearchInput(Array.isArray(value) && value[0] ? value[0] : '');
        break;
      case 'status':
        if (!Array.isArray(value)) return;
        setStatusFilter(value[0] ?? '');
        setPage(1);
        break;
      case 'vendor':
        if (!Array.isArray(value)) return;
        setVendorFilter(value[0] ?? '');
        setPage(1);
        break;
    }
  }, []);

  const handleSidebarClear = useCallback((filterKey: string) => {
    switch (filterKey) {
      case 'search':
        setSearchInput('');
        break;
      case 'status':
        setStatusFilter('');
        setPage(1);
        break;
      case 'vendor':
        setVendorFilter('');
        setPage(1);
        break;
    }
  }, []);

  // ── DataTable columns ──────────────────────────────────────────────────────

  const columns = useMemo<DataTableColumn<PurchaseOrder>[]>(
    () => [
      {
        key: 'orderNumber',
        header: 'PO #',
        render: (po) => (
          <span className="font-medium text-slate-900">
            {po.orderNumber || `PO-${po.purchaseOrderId}`}
          </span>
        ),
      },
      {
        key: 'vendorName',
        header: 'Vendor',
        render: (po) => (
          <span className="text-slate-700">
            {po.vendorName ?? vendorMap.get(po.vendorId)?.name ?? `Vendor #${po.vendorId}`}
          </span>
        ),
      },
      {
        key: 'status',
        header: 'Status',
        render: (po) => (
          <span
            className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${STATUS_BADGE_CLASSES[po.status]}`}
          >
            {statusLabel(po.status)}
          </span>
        ),
      },
      {
        key: 'orderDate',
        header: 'Order Date',
        render: (po) => <span className="text-slate-600">{formatDate(po.orderDate)}</span>,
      },
      {
        key: 'expectedDate',
        header: 'Expected Date',
        render: (po) => <span className="text-slate-600">{formatDate(po.expectedDate)}</span>,
      },
      {
        key: 'totalCost',
        header: 'Total Cost',
        // `align` is what DataTable actually reads; the className/headerClassName
        // props it used to pass are not declared on DataTableColumn and were
        // silently dropped, so neither column was ever right-aligned.
        align: 'right',
        render: (po) => (
          <span className="font-medium text-slate-900">{formatCurrency(po.totalCost)}</span>
        ),
      },
      {
        key: 'items',
        header: 'Items',
        align: 'right',
        render: (po) => <span className="text-slate-700">{po.items?.length ?? 0}</span>,
      },
    ],
    [vendorMap],
  );

  // ── Row actions ────────────────────────────────────────────────────────────

  const rowActions = useMemo<DataTableAction<PurchaseOrder>[]>(() => [
    {
      label: 'Submit',
      onClick: (po) => submitMutation.mutate(po.purchaseOrderId),
      disabled: (po) => po.status !== 'draft' || submitMutation.isPending,
    },
    {
      label: 'Receive',
      onClick: (po) => setReceiveTarget(po),
      disabled: (po) => po.status !== 'submitted' && po.status !== 'partially_received',
    },
    {
      label: 'Cancel',
      variant: 'danger',
      onClick: (po) => setCancelTarget(po),
      disabled: (po) => po.status !== 'draft' && po.status !== 'submitted',
    },
  ], [submitMutation]);

  // ─── Render ─────────────────────────────────────────────────────────────────

  return (
    <>
      <ListPageLayout
        filterSidebar={(
          <FilterSidebar
            filters={FILTER_CONFIGS}
            values={sidebarValues}
            options={sidebarOptions}
            onChange={handleSidebarChange}
            onClear={handleSidebarClear}
            showAIAssistant={false}
          />
        )}
      >
        <div className="flex h-full min-h-0 flex-col">
          <div className="shrink-0 space-y-6 p-6">
            <PageHeader
              variant="compact"
              title="Purchase Orders"
              actions={(
                <div className="flex items-center gap-2">
                  <Button
                    variant="secondary"
                    leftIcon={<Download className="h-4 w-4" />}
                    onClick={handleExport}
                    disabled={(meta?.total ?? 0) === 0 || exporting}
                    loading={exporting}
                  >
                    Export
                  </Button>
                  {exportModal}
                  <Button
                    variant="primary"
                    leftIcon={<Plus className="h-4 w-4" />}
                    onClick={() => setCreateOpen(true)}
                    disabled={!vendorsLoading && vendors.length === 0}
                    title={
                      !vendorsLoading && vendors.length === 0
                        ? 'Add a vendor first — a purchase order is placed with a vendor.'
                        : undefined
                    }
                  >
                    New Purchase Order
                  </Button>
                </div>
              )}
            />

            <p className="text-sm text-slate-500">
              Purchase orders record what you ordered and what arrived. Receiving one
              does not change stock on hand — book received inventory in on the{' '}
              <button
                type="button"
                className="font-medium text-blue-600 underline underline-offset-2 hover:text-blue-700"
                onClick={() => navigate('/inventory/stock')}
              >
                Stock page
              </button>
              : open the product&apos;s <strong>Adjust Stock</strong> and choose <strong>Add Stock</strong>.
            </p>

            {!vendorsLoading && vendors.length === 0 && (
              <p className="text-sm text-slate-500">
                No vendors yet.{' '}
                <button
                  type="button"
                  className="font-medium text-blue-600 underline underline-offset-2 hover:text-blue-700"
                  onClick={() => navigate('/inventory/vendors')}
                >
                  Add a vendor
                </button>{' '}
                before creating a purchase order.
              </p>
            )}

            <MetricCardGrid columns={4}>
              <StatCard
                label="Total POs"
                value={stats?.total ?? 0}
              />
              <StatCard
                label="Draft"
                value={stats?.draft ?? 0}
              />
              <StatCard
                label="Submitted"
                value={stats?.submitted ?? 0}
              />
              <StatCard
                label="Received"
                value={stats?.received ?? 0}
              />
            </MetricCardGrid>

          </div>

          <div className="min-h-0 flex-1 overflow-auto">
            <DataTable<PurchaseOrder>
              data={poList}
              columns={columns}
              keyField="purchaseOrderId"
              loading={poQuery.isLoading}
              rowActions={rowActions}
              page={page}
              pageSize={PER_PAGE}
              total={meta?.total ?? 0}
              onPageChange={setPage}
              emptyTitle="No purchase orders found"
              emptyDescription="Try adjusting the filters or create a new purchase order."
            />
          </div>
        </div>
      </ListPageLayout>

      {/* ── Create PO Modal ── */}
      {createOpen && (
        <CreatePOModal
          vendors={vendors}
          products={products}
          onClose={() => setCreateOpen(false)}
          onCreated={() => {
            setCreateOpen(false);
            invalidatePOs();
          }}
        />
      )}

      {/* ── Receive PO Modal ── */}
      {receiveTarget && (
        <ReceivePOModal
          po={receiveTarget}
          onClose={() => setReceiveTarget(null)}
          onReceived={() => {
            setReceiveTarget(null);
            invalidatePOs();
          }}
        />
      )}

      {/* ── Cancel Confirm Dialog ── */}
      {cancelTarget && (
        <CancelConfirmDialog
          po={cancelTarget}
          isPending={cancelMutation.isPending}
          onClose={() => setCancelTarget(null)}
          onConfirm={() => cancelMutation.mutate(cancelTarget.purchaseOrderId)}
        />
      )}
    </>
  );
}

// ─── Create PO Modal ──────────────────────────────────────────────────────────

interface CreatePOModalProps {
  vendors: Vendor[];
  products: Product[];
  onClose: () => void;
  onCreated: () => void;
}

function CreatePOModal({ vendors, products, onClose, onCreated }: CreatePOModalProps) {
  /**
   * The sheet utilities on the panel below read `--vv-height` / `--vv-offset-top`, which nothing
   * sets on its own — so the hook is required, not optional decoration. It is what makes the
   * modal track the visible viewport when the on-screen keyboard opens; `max-h-[90vh]` alone is
   * measured against the layout viewport, which the keyboard does not shrink.
   *
   * Unconditional and at the top of the component. A subscription hook behind a condition or an
   * early return changes the render-to-render hook count, which is the React #310 shape.
   */
  const isPhone = useIsBelow('md');
  useVisualViewportVars(isPhone);
  useBodyScrollLock(true);

  const [vendorId, setVendorId] = useState<number | ''>('');
  const [orderNumber, setOrderNumber] = useState('');
  const [orderDate, setOrderDate] = useState(new Date().toISOString().slice(0, 10));
  const [expectedDate, setExpectedDate] = useState('');
  const [notes, setNotes] = useState('');
  const [lineItems, setLineItems] = useState<LineItem[]>([newLineItem()]);
  const [serverError, setServerError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (payload: CreatePOPayload) => createPurchaseOrder(payload),
    onSuccess: () => onCreated(),
    onError: (err) => {
      setServerError(getErrorMessage(err, 'The purchase order could not be created. Please try again.'));
    },
  });

  // Computed total
  const totalCost = useMemo(() => {
    return lineItems.reduce((sum, item) => {
      const qty = typeof item.quantity === 'number' ? item.quantity : 0;
      const cost = typeof item.unitCost === 'number' ? item.unitCost : 0;
      return sum + qty * cost;
    }, 0);
  }, [lineItems]);

  function addLineItem() {
    setLineItems((prev) => [...prev, newLineItem()]);
  }

  function removeLineItem(key: number) {
    setLineItems((prev) => {
      if (prev.length <= 1) return prev;
      return prev.filter((item) => item.key !== key);
    });
  }

  function updateLineItem(key: number, field: keyof LineItem, value: number | string) {
    setLineItems((prev) =>
      prev.map((item) => (item.key === key ? { ...item, [field]: value } : item)),
    );
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setServerError(null);

    if (!vendorId) {
      setServerError('Please select a vendor.');
      return;
    }

    const validItems = lineItems.filter(
      (li) => li.productId !== '' && li.quantity !== '' && li.unitCost !== '',
    );
    if (validItems.length === 0) {
      setServerError('Please add at least one line item with product, quantity, and unit cost.');
      return;
    }
    if (validItems.some((li) => !Number.isInteger(li.quantity))) {
      setServerError(WHOLE_QUANTITIES_MESSAGE);
      return;
    }

    const payload: CreatePOPayload = {
      vendorId: vendorId as number,
      orderNumber: orderNumber.trim() || undefined,
      orderDate: orderDate || undefined,
      expectedDate: expectedDate || undefined,
      notes: notes.trim() || undefined,
      items: validItems.map((li) => ({
        productId: li.productId as number,
        quantityOrdered: li.quantity as number,
        unitCost: li.unitCost as number,
      })),
    };

    mutation.mutate(payload);
  }

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center ${PHONE_SHEET_WRAPPER} max-md:items-stretch`}
    >
      <div className="absolute inset-0 bg-black/40" onClick={mutation.isPending ? undefined : onClose} />
      <div
        className={`relative z-10 bg-white rounded-xl shadow-xl max-w-2xl w-full mx-4 flex flex-col max-h-[90vh] ${PHONE_SHEET_PANEL} max-md:mx-0 max-md:rounded-b-none`}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <h2 className="text-lg font-semibold text-slate-900">New Purchase Order</h2>
          <button
            type="button"
            onClick={onClose}
            disabled={mutation.isPending}
            className="text-slate-400 hover:text-slate-600 transition-colors text-xl leading-none touch-target-44 tap-feedback"
            aria-label="Close"
          >
            &times;
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto px-6 py-5 flex-1">
          <form id="create-po-form" onSubmit={handleSubmit} noValidate className="space-y-5">
            {serverError && (
              <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">
                {serverError}
              </div>
            )}

            {/* Vendor */}
            <div>
              <label htmlFor="po-vendor" className={labelCls}>
                Vendor <span className="text-red-500">*</span>
              </label>
              <select
                id="po-vendor"
                value={vendorId}
                onChange={(e) => setVendorId(e.target.value ? Number(e.target.value) : '')}
                className={inputCls}
              >
                <option value="">Select a vendor...</option>
                {vendors.map((v) => (
                  <option key={v.vendorId} value={v.vendorId}>
                    {v.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Order number + dates. Unprefixed `grid-cols-3` gave each column ~87px at 375px
                before `px-3`, which truncated the "Auto-generated" placeholder to "Auto-gene" and
                pushed Expected Date past the modal's right edge. */}
            <div className="grid grid-cols-3 gap-4 max-md:grid-cols-1">
              <div>
                <label htmlFor="po-number" className={labelCls}>
                  Order Number
                </label>
                <input
                  id="po-number"
                  type="text"
                  value={orderNumber}
                  onChange={(e) => setOrderNumber(e.target.value)}
                  className={inputCls}
                  placeholder="Auto-generated"
                />
              </div>
              <div>
                <label htmlFor="po-order-date" className={labelCls}>
                  Order Date
                </label>
                {/* Was a native date input on the local `inputCls`, which reaches neither
                    `form-input-base` nor any of index.css's date rules — so it kept the UA's own
                    grey inset box beside the white Notes and Qty fields, and had no calendar icon.
                    DatePicker supplies both, plus the placeholder a native control ignores. */}
                <DatePicker
                  id="po-order-date"
                  value={orderDate}
                  onChange={setOrderDate}
                  clearable
                  placeholder="Select a date"
                />
              </div>
              <div>
                <label htmlFor="po-expected-date" className={labelCls}>
                  Expected Date
                </label>
                <DatePicker
                  id="po-expected-date"
                  value={expectedDate}
                  onChange={setExpectedDate}
                  clearable
                  placeholder="Select a date"
                />
              </div>
            </div>

            {/* Notes */}
            <div>
              <label htmlFor="po-notes" className={labelCls}>
                Notes
              </label>
              <textarea
                id="po-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={2}
                className={inputCls}
                placeholder="Optional notes..."
              />
            </div>

            {/* Line Items */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className={labelCls + ' mb-0'}>
                  Line Items <span className="text-red-500">*</span>
                </label>
                <button type="button" onClick={addLineItem} className="text-sm text-indigo-600 hover:text-indigo-700 font-medium">
                  + Add Item
                </button>
              </div>

              <div className="space-y-2">
                {/* Column headers */}
                {/* Hidden below `md`, where each field carries its own aria-label and placeholder
                    instead — a three-column header over a stacked row labels nothing. */}
                <div className="grid grid-cols-[1fr_100px_120px_36px] gap-2 text-xs font-medium text-slate-500 px-1 max-md:hidden">
                  <span>Product</span>
                  <span>Qty</span>
                  <span>Unit Cost</span>
                  <span />
                </div>

                {lineItems.map((li) => (
                  /* Qty (100px), Unit Cost (120px), the delete button (36px) and three gaps take
                     280px before the `1fr` Product track gets anything — at 375px that left the
                     select ~15px, i.e. "Sel". Two columns below `md`, with Product spanning both
                     so a product name is readable. */
                  <div
                    key={li.key}
                    className="grid grid-cols-[1fr_100px_120px_36px] gap-2 items-center max-md:grid-cols-2"
                  >
                    <select
                      aria-label="Product"
                      value={li.productId}
                      onChange={(e) =>
                        updateLineItem(li.key, 'productId', e.target.value ? Number(e.target.value) : '')
                      }
                      className={`${inputCls} max-md:col-span-2`}
                    >
                      <option value="">Select product...</option>
                      {products.map((p) => (
                        <option key={p.productId} value={p.productId}>
                          {p.name} {p.sku ? `(${p.sku})` : ''}
                        </option>
                      ))}
                    </select>
                    <input
                      aria-label="Quantity"
                      type="number"
                      min="1"
                      step="1"
                      placeholder="0"
                      value={li.quantity}
                      onChange={(e) =>
                        updateLineItem(li.key, 'quantity', e.target.value ? Number(e.target.value) : '')
                      }
                      className={inputCls}
                    />
                    <input
                      aria-label="Unit cost"
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="0.00"
                      value={li.unitCost}
                      onChange={(e) =>
                        updateLineItem(li.key, 'unitCost', e.target.value ? Number(e.target.value) : '')
                      }
                      className={inputCls}
                    />
                    <button
                      type="button"
                      onClick={() => removeLineItem(li.key)}
                      disabled={lineItems.length <= 1}
                      className="flex items-center justify-center h-9 w-9 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-slate-400"
                      title="Remove item"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>

              {/* Total */}
              <div className="mt-3 flex justify-end">
                <div className="rounded-lg bg-slate-50 border border-slate-200 px-4 py-2 text-sm">
                  <span className="text-slate-500">Total: </span>
                  <span className="font-semibold text-slate-900">{formatCurrency(totalCost)}</span>
                </div>
              </div>
            </div>
          </form>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-200">
          <button
            type="button"
            onClick={onClose}
            disabled={mutation.isPending}
            className={btnSecondary}
          >
            Cancel
          </button>
          <button
            type="submit"
            form="create-po-form"
            disabled={mutation.isPending}
            className={btnPrimary}
          >
            {mutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            {mutation.isPending ? 'Creating...' : 'Create Purchase Order'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Receive PO Modal ─────────────────────────────────────────────────────────

interface ReceivePOModalProps {
  po: PurchaseOrder;
  onClose: () => void;
  onReceived: () => void;
}

function ReceivePOModal({ po, onClose, onReceived }: ReceivePOModalProps) {
  // Same reasoning as CreatePOModal — see the note there. Both modals on this page are
  // hand-rolled with the same `max-h-[90vh]` shell and the same boxless close glyph, so they get
  // the same treatment rather than one being fixed and its twin left for the next pass.
  const isPhone = useIsBelow('md');
  useVisualViewportVars(isPhone);
  useBodyScrollLock(true);

  const [receiveQtys, setReceiveQtys] = useState<Record<number, number>>(() => {
    const init: Record<number, number> = {};
    for (const item of po.items ?? []) {
      init[item.poItemId] = 0;
    }
    return init;
  });
  const [serverError, setServerError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (payload: ReceiveItemsPayload) => receivePurchaseOrder(po.purchaseOrderId, payload),
    onSuccess: () => onReceived(),
    onError: (err) => {
      setServerError(getErrorMessage(err, 'The items could not be received. Please try again.'));
    },
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setServerError(null);

    const items = Object.entries(receiveQtys)
      .filter(([, qty]) => qty > 0)
      .map(([poItemId, qty]) => ({
        poItemId: Number(poItemId),
        quantityReceived: qty,
      }));

    if (items.length === 0) {
      setServerError('Enter a quantity to receive for at least one item.');
      return;
    }
    if (items.some((i) => !Number.isInteger(i.quantityReceived))) {
      setServerError(WHOLE_QUANTITIES_MESSAGE);
      return;
    }
    // The form is noValidate, so the inputs' max={remaining} is not enforced.
    const over = (po.items ?? []).find(
      (line) => (receiveQtys[line.poItemId] ?? 0) > line.quantityOrdered - line.quantityReceived,
    );
    if (over) {
      const left = over.quantityOrdered - over.quantityReceived;
      setServerError(
        `${over.productName || 'A line'}: only ${left} more can be received — ${over.quantityOrdered} were ordered and ${over.quantityReceived} have arrived.`,
      );
      return;
    }

    mutation.mutate({ items });
  }

  function updateQty(poItemId: number, value: number) {
    setReceiveQtys((prev) => ({ ...prev, [poItemId]: value }));
  }

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center ${PHONE_SHEET_WRAPPER} max-md:items-stretch`}
    >
      <div className="absolute inset-0 bg-black/40" onClick={mutation.isPending ? undefined : onClose} />
      <div
        className={`relative z-10 bg-white rounded-xl shadow-xl max-w-xl w-full mx-4 flex flex-col max-h-[90vh] ${PHONE_SHEET_PANEL} max-md:mx-0 max-md:rounded-b-none`}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">Receive Items</h2>
            <p className="text-sm text-slate-500">{po.orderNumber || `PO-${po.purchaseOrderId}`}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={mutation.isPending}
            className="text-slate-400 hover:text-slate-600 transition-colors text-xl leading-none touch-target-44 tap-feedback"
            aria-label="Close"
          >
            &times;
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto px-6 py-5 flex-1">
          <form id="receive-po-form" onSubmit={handleSubmit} noValidate className="space-y-4">
            {serverError && (
              <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">
                {serverError}
              </div>
            )}

            <p className="text-sm text-slate-500">
              This records what arrived against the order. It does not change stock on
              hand — book the delivery in on the Stock page: open the product&apos;s{' '}
              <strong>Adjust Stock</strong> and choose <strong>Add Stock</strong>.
            </p>

            <div className="overflow-x-auto rounded-lg border border-slate-200">
              <table className="w-full text-sm text-left">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="px-3 py-2 font-medium text-slate-600">Product</th>
                    <th className="px-3 py-2 font-medium text-slate-600 text-right">Ordered</th>
                    <th className="px-3 py-2 font-medium text-slate-600 text-right">Received</th>
                    <th className="px-3 py-2 font-medium text-slate-600 text-right">Remaining</th>
                    <th className="px-3 py-2 font-medium text-slate-600 text-right">Receive Now</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {(po.items ?? []).map((item) => {
                    const remaining = item.quantityOrdered - item.quantityReceived;
                    return (
                      <tr key={item.poItemId} className="hover:bg-slate-50">
                        <td className="px-3 py-2 text-slate-900 font-medium">
                          {item.productName || `Product #${item.productId}`}
                        </td>
                        <td className="px-3 py-2 text-right text-slate-700">
                          {item.quantityOrdered}
                        </td>
                        <td className="px-3 py-2 text-right text-slate-700">
                          {item.quantityReceived}
                        </td>
                        <td className="px-3 py-2 text-right text-slate-700">
                          {remaining}
                        </td>
                        <td className="px-3 py-2 text-right">
                          <input
                            type="number"
                            min="0"
                            max={remaining}
                            step="1"
                            value={receiveQtys[item.poItemId] || ''}
                            onChange={(e) =>
                              updateQty(item.poItemId, Math.max(0, Number(e.target.value)))
                            }
                            disabled={remaining <= 0}
                            className="w-20 border border-slate-300 rounded-lg px-2 py-1.5 text-sm text-right focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:opacity-50 disabled:bg-slate-50"
                            placeholder="0"
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </form>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-200">
          <button
            type="button"
            onClick={onClose}
            disabled={mutation.isPending}
            className={btnSecondary}
          >
            Cancel
          </button>
          <button
            type="submit"
            form="receive-po-form"
            disabled={mutation.isPending}
            className={btnPrimary}
          >
            {mutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            {mutation.isPending ? 'Receiving...' : 'Receive Items'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Cancel Confirm Dialog ────────────────────────────────────────────────────

interface CancelConfirmDialogProps {
  po: PurchaseOrder;
  isPending: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

function CancelConfirmDialog({ po, isPending, onClose, onConfirm }: CancelConfirmDialogProps) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={isPending ? undefined : onClose} />
      <div className="relative z-10 bg-white rounded-xl shadow-xl max-w-sm w-full mx-4">
        <div className="px-6 py-5">
          <h3 className="text-lg font-semibold text-slate-900 mb-2">Cancel Purchase Order</h3>
          <p className="text-sm text-slate-600">
            Are you sure you want to cancel{' '}
            <span className="font-medium">{po.orderNumber || `PO-${po.purchaseOrderId}`}</span>?
            This action cannot be undone.
          </p>
        </div>
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-200">
          <button
            type="button"
            onClick={onClose}
            disabled={isPending}
            className={btnSecondary}
          >
            Keep Order
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={isPending}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors disabled:opacity-50"
          >
            {isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            {isPending ? 'Cancelling...' : 'Yes, Cancel Order'}
          </button>
        </div>
      </div>
    </div>
  );
}
